<template>
  <RouterLink
    v-if="isRouterLink"
    :class="cardClasses"
    :to="postObj.routeTo"
    :title="cardTitle"
    :aria-label="cardTitle"
    :style="cardStyles"
  >
    <div :class="innerClasses">
      <div :class="overlayClasses" />
      <div :class="textWrapperClasses">
        <span
          :class="spanClasses"
          v-html="spanText"
        />
        <SvgIcon
          :class="iconClasses"
          :icon="iconSlug"
        />
      </div>
    </div>
  </RouterLink>
  <a
    v-else-if="isLink"
    :class="cardClasses"
    :href="cardHref"
    :target="cardTarget"
    :title="cardTitle"
    :aria-label="cardTitle"
    :style="cardStyles"
  >
    <div :class="innerClasses">
      <div :class="overlayClasses" />
      <div :class="textWrapperClasses">
        <span
          :class="spanClasses"
          v-html="spanText"
        />
        <SvgIcon
          :class="iconClasses"
          :icon="iconSlug"
        />
      </div>
    </div>
  </a>
  <button
    v-else-if="isButton"
    :class="cardClasses"
    :target="cardTarget"
    :title="cardTitle"
    :aria-label="cardTitle"
    :style="cardStyles"
    @click="buttonClick"
  >
    <div :class="innerClasses">
      <div :class="overlayClasses" />
      <div :class="textWrapperClasses">
        <span
          :class="spanClasses"
          v-html="spanText"
        />
        <SvgIcon
          :class="iconClasses"
          :icon="iconSlug"
        />
      </div>
    </div>
  </button>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'DirectCard',

  components: {
    SvgIcon
  },

  props: {
    postObj: {
      type: Object,
      default: () => {}
    },
    largeSize: {
      type: Boolean,
      default: undefined
    },
  },

  computed: {
    ...mapState([
      'options',
      'lteMobile',
      'lteTablet',
    ]),
    ...mapGetters([
      'myStore',
    ]),
    // Click
    clickBehavior() {
      const { postObj } = this
      const { click_behavior } = postObj
      return click_behavior || 'new-tab'
    },
    isModal() {
      const { clickBehavior } = this
      return 'modal' === clickBehavior
    },
    isSameTab() {
      const { clickBehavior } = this
      return 'same-tab' === clickBehavior
    },
    isExternalLink() {
      const { postObj } = this
      const { type } = this
      return 'external-link' === type
    },
    isMedia() {
      const { postObj } = this
      const { type } = postObj
      return ['group', 'panorama', 'article', 'video', 'gallery'].indexOf( type ) !== -1
    },
    // Config
    isRouterLink() {
      const { postObj } = this
      const { routeTo } = postObj
      return routeTo
    },
    isLink() {
      const { postObj, lteMobile, isModal } = this
      const { url } = postObj
      const isValidModal = isModal && !lteMobile
      return url && !isValidModal
    },
    isButton() {
      const { postObj, lteMobile, isModal } = this
      const { url } = postObj
      const isValidModal = isModal && !lteMobile
      return url && isValidModal
    },
    // Data
    cardImageUrl() {
      const { postObj, largeSize, options, lteTablet } = this
      const useBiggerImage = largeSize && !lteTablet
      const { card_image } = postObj || {}
      const { placeholder_card_image } = options || {}
      const cardObj = card_image || placeholder_card_image
      const { sizes, url } = cardObj || {}
      const { medium_large, medium } = sizes || {}
      return (useBiggerImage ? medium_large : medium) || url
    },
    cardStyles() {
      const { cardImageUrl } = this
      const styles = {}
      if (cardImageUrl) {
        styles.backgroundImage = `url('${cardImageUrl}')`
      }
      return styles
    },
    cardTarget() {
      const { isSameTab, isLink } = this
      const useBlank = isLink && !isSameTab
      return useBlank ? '_blank' : '_self'
    },
    cardTitle() {
      const { postObj, htmlDecode } = this
      const { btn_title, btn_text, title } = postObj
      const validText = btn_title || btn_text || title
      return htmlDecode( validText )
    },
    cardHref() {
      const { postObj, isExternalLink, utmHref } = this
      const { url } = postObj
      if (!url) { return }
      const utmContent = isExternalLink ? 'external-link-card' : 'tier-2-cta'
      return utmHref( url, utmContent )
    },
    // Styles
    cardSettings() {
      const { myStore } = this
      return myStore(['styles', 'cards']) || {}
    },
    focusStyles() {
      const { cardSettings } = this
      const { focus } = cardSettings
      return focus || {}
    },
    hoverStyles() {
      const { cardSettings } = this
      const { hover } = cardSettings
      return hover || {}
    },
    // cardClasses
    cardClasses() {
      const { cardSettings, focusStyles, hoverStyles } = this
      const { border_radius, text_color, fill_color } = cardSettings
      // Return
      return [
        'DirectCardComp',
        'group',
        // Position
        'relative',
        // Dimensions
        'w-full',
        'h-60',
        // Display
        'overflow-hidden',
        'flex-c',
        // Border
        `rounded-${border_radius ?? 'none'}`,
        // Bg
        `bg-black bg-full`,
        // Text
        `text-${text_color || 'white'}`,
        `focus:text-${focusStyles.text_color} lg:hover:text-${hoverStyles.text_color}`,
        // Fill
        `fill-${fill_color || 'white'}`,
        `focus:fill-${focusStyles.fill_color} lg:hover:fill-${hoverStyles.fill_color}`,
        // Outline
        'focus:outline-none',
        // Transitions
        'transition-colors duration-500 ease-in-out'
      ]
    },
    innerClasses() {
      const { largeSize, lteTablet, cardSettings } = this
      const { border_radius } = cardSettings
      const useBorderRadius = largeSize && !lteTablet && border_radius
      const borderRadiusClass = useBorderRadius ? `rounded-tr-${border_radius}` : ''
      return [
        // Position
        'absolute',
        'left-0 bottom-0',
        // Dimensions
        'w-full',
        'lg:max-w-62_5',
        // Overflow
        'overflow-hidden',
        // Border
        borderRadiusClass,
      ]
    },
    overlayClasses() {
      const { cardSettings, focusStyles, hoverStyles } = this
      const { bg_color, opacity } = cardSettings
      return [
        // Position
        // Dimensions
        'a-cover',
        // Bg
        `bg-${bg_color}`,
        `group-focus:bg-${focusStyles.bg_color} lg:group-hover:bg-${hoverStyles.bg_color}`,
        // Opacity
        `opacity-${opacity ?? 80}`,
        // Transition
        'transition-colors duration-500 ease-in-out'
      ]
    },
    textWrapperClasses() {
      return [
        // Position
        'r-cover',
        // Padding
        'px-4 py-2_25',
        // Display
        'flex justify-between items-start'
      ]
    },
    spanClasses() {
      const { cardSettings } = this
      const { font_family, font_weight, text_transform } = cardSettings
      const fontFamilyVal = font_family || 'body'
      const fontFamilyClass = `font-${fontFamilyVal}`
      const fontWeightVal = font_family || 'normal'
      const fontWeightClass = `${fontFamilyClass}-${fontWeightVal}`
      const textTransformClass = text_transform || 'normal-case'
      // Return
      return [
        'w-full',
        // Font
        fontFamilyClass,
        fontWeightClass,
        // Line height
        'leading-thin',
        // Text transform
        textTransformClass,
      ]
    },
    spanText() {
      const { postObj } = this
      const { btn_text, card_text, title } = postObj
      return btn_text || card_text || title
    },
    iconClasses() {
      return [
        // Dimensions
        'w-6 h-6',
        // Margin
        'ml-3',
        // Display
        'flex-shrink-0'
      ]
    },
    iconSlug() {
      const { postObj, isMedia } = this
      const { btn_icon, type } = postObj
      const fallbackIcon = isMedia ? type : 'arrow-right'
      return btn_icon || fallbackIcon
    }
  },

  methods: {
    buttonClick() {
      const { $store, isModal, cardHref } = this
      // Conent Modal
      if (isModal) {
        $store.commit('contentModal', cardHref)
      }
    }
  }
}
</script>

